import { useTranslation } from "react-i18next";
import { FiCpu, FiMonitor, FiSmartphone, FiTablet, FiTerminal } from "react-icons/fi";
import {
  getMostCommonCompactTitle,
  getMostCommonDisplayValue,
  getMostCommonIconKind,
  getMostCommonSkeletonItems,
  getMostCommonSkeletonLayout,
  useMostCommonContainerWidth,
  useMostCommonLayout,
  useUbiquitiMostCommonDevices,
} from "./UbiquitiOverview.logic";

const MostCommonIcon = ({ row }) => {
  const iconKind = getMostCommonIconKind(row);

  if (iconKind === "smartphone") {
    return <FiSmartphone />;
  }

  if (iconKind === "tablet") {
    return <FiTablet />;
  }

  if (iconKind === "terminal") {
    return <FiTerminal />;
  }

  if (iconKind === "monitor") {
    return <FiMonitor />;
  }

  return <FiCpu />;
};

const MostCommonTooltip = ({ row }) => (
  <div className="ubiquiti-most-common-tooltip" role="tooltip">
    <div className="ubiquiti-most-common-tooltip-title">
      <span className="ubiquiti-most-common-tooltip-icon">
        <MostCommonIcon row={row} />
      </span>
      <span>{getMostCommonDisplayValue(row.typeDisplay)}</span>
    </div>
    {row.periodDisplay ? (
      <small>{row.periodDisplay}</small>
    ) : null}
    <dl>
      <div>
        <dt>Device Count</dt>
        <dd>{getMostCommonDisplayValue(row.deviceCount)}</dd>
      </div>
    </dl>
  </div>
);

const MostCommonItem = ({ row }) => {
  const title = getMostCommonDisplayValue(row.typeDisplay);

  return (
    <div className="ubiquiti-most-common-item" aria-label={title} tabIndex={0} title={title}>
      <span className="ubiquiti-most-common-count">{getMostCommonDisplayValue(row.deviceCount)}</span>
      <span className="ubiquiti-most-common-icon">
        <MostCommonIcon row={row} />
      </span>
      <span className="ubiquiti-most-common-label">{getMostCommonCompactTitle(title)}</span>
      <MostCommonTooltip row={row} />
    </div>
  );
};

const UbiquitiMostCommonDevices = ({ apiUrl, enabled = true, onQueryStateChange, refreshKey = 0, siteId }) => {
  const { t } = useTranslation();
  const { isInitialLoading, rows } = useUbiquitiMostCommonDevices({
    apiUrl,
    enabled,
    onQueryStateChange,
    refreshKey,
    siteId,
  });
  const { containerRef, containerWidth } = useMostCommonContainerWidth();
  const skeletonItems =
    getMostCommonSkeletonItems(containerWidth);

  const skeletonLayout =
    getMostCommonSkeletonLayout(containerWidth);

  const {
    visibleRows,
    isConstrained: contentIsConstrained,
  } = useMostCommonLayout(
    rows,
    containerWidth,
  );

  const isConstrained = isInitialLoading
    ? skeletonLayout.isConstrained
    : contentIsConstrained;

  return (
    <article
      className={`ubiquiti-most-common-card${isConstrained ? " is-constrained" : ""}`}
      ref={containerRef}
    >
      <div className="ubiquiti-top-title">Most Common Devices</div>
      <div className="ubiquiti-most-common-items">
        {isInitialLoading ? skeletonItems.map((itemKey) => (
          <div className="ubiquiti-most-common-item is-loading" key={itemKey}>
            <span className="ubiquiti-most-common-count"></span>
            <span className="ubiquiti-most-common-icon"></span>
            <span className="ubiquiti-most-common-label"></span>
          </div>
        )) : visibleRows.length > 0 ? visibleRows.map((row, index) => (
          <MostCommonItem row={row} key={`${row.type || row.typeDisplay || "unknown"}-${index}`} />
        )) : (
          <div className="ubiquiti-empty-inline">{t("No data available")}</div>
        )}
      </div>
    </article>
  );
};

export default UbiquitiMostCommonDevices;
